import { useContext, useEffect, useMemo, useState } from "react"
import firebase from "firebase"
import moment from "moment"
import random from "randomstring"
import { Pencil, Plus, Trash2 } from "lucide-react"

import { db } from "../../../../utils/firebase"
import { AlertsContext } from "../../../../utils/providers/alerts"
import { AuthContext } from "../../../../utils/providers/auth"
import { ModalContext } from "../../../../utils/providers/modal"
import Button from "../../../ui/button/button"
import Input from "../../../ui/inputs/input"
import Select from "../../../ui/select/select"
import Table from "../../../structure/table/table"
import Window from "../../../structure/window/window"
import "./contacts.scss"

// Single source of truth for the dropdown — used both for picking and for
// rendering the saved value back as a label.
const CONTACT_TYPES = [
	{ value: "reservations", label: "Reservations" },
	{ value: "sales", label: "Sales" },
	{ value: "accounts", label: "Accounts" },
	{ value: "pro_shop", label: "Pro Shop" },
	{ value: "hotel_reception", label: "Hotel Reception" },
	{ value: "marketing_manager", label: "Marketing Manager" },
	{ value: "general_manager", label: "General Manager" },
	{ value: "operations", label: "Operations" },
	{ value: "groups", label: "Group Bookings" },
	{ value: "other", label: "Other" },
]

// Shape the type list for our custom Select component, which expects an
// object map of { value: label }.
const CONTACT_TYPE_OPTIONS = CONTACT_TYPES.reduce((acc, type) => {
	acc[type.value] = type.label
	return acc
}, {})

const COLLECTION_FOR_KIND = {
	CMS_HOLIDAY: "CMS_holidays",
	CMS_COURSE: "CMS_courses",
}

const formatTimestamp = (timestamp) => {
	const seconds = timestamp?.seconds
	if (!seconds) return null
	return moment.unix(seconds).format("DD MMM YYYY HH:mm")
}

const labelForType = (type) =>
	CONTACT_TYPES.find((entry) => entry.value === type)?.label || null

export default function SupplierContacts({ supplierKind, supplierId }) {
	const { user } = useContext(AuthContext)
	const { pushAlert } = useContext(AlertsContext)
	const { showModal } = useContext(ModalContext)

	const [contacts, setContacts] = useState([])
	const [loading, setLoading] = useState(true)
	const [editing, setEditing] = useState(null)
	const [adding, setAdding] = useState(false)

	const collectionName = COLLECTION_FOR_KIND[supplierKind]
	const contactsPath = collectionName && supplierId
		? `${collectionName}/${supplierId}/contacts`
		: null

	useEffect(() => {
		if (!contactsPath) return
		setLoading(true)
		const unsubscribe = db
			.collection(contactsPath)
			.onSnapshot(
				(snapshot) => {
					setContacts(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })))
					setLoading(false)
				},
				(err) => {
					console.error("Failed to load supplier contacts", err)
					setLoading(false)
				}
			)
		return () => unsubscribe()
	}, [contactsPath])

	const sortedContacts = useMemo(() => {
		// Most-recently-touched first so staff can act on fresh parses without
		// scrolling.
		return [...contacts].sort((a, b) => {
			const aSeen = a.updated_at?.seconds || a.created_at?.seconds || 0
			const bSeen = b.updated_at?.seconds || b.created_at?.seconds || 0
			return bSeen - aSeen
		})
	}, [contacts])

	const confirmDelete = (contact) => {
		showModal({
			type: "ALERT",
			title: "Delete this contact?",
			body: "This contact will be removed from the location. If it appears in a future parsed document, it will be re-added automatically.",
			cancel: {
				label: "Cancel",
				action: () => null,
			},
			next: {
				label: "Yes, delete",
				action: async () => {
					try {
						await db.doc(`${contactsPath}/${contact.id}`).delete()
					} catch (err) {
						pushAlert({
							type: "ERROR",
							title: "Failed to delete contact",
							body: err?.message || String(err),
						})
					}
				},
			},
		})
	}

	const headings = [
		{ key: "last_updated", label: "Last updated" },
		{ key: "name", label: "Name" },
		{ key: "email", label: "Email" },
		{ key: "phone", label: "Phone" },
		{ key: "type", label: "Type" },
		{ key: "source", label: "Source" },
		{ key: "actions", label: "" },
	]

	return (
		<div className="supplier-contacts">
			<div className="supplier-contacts-header">
				<Button
					label="Add contact"
					icon={<Plus size={14} />}
					onClick={() => setAdding(true)}
				/>
			</div>

			<Table
				className="supplier-contacts-table"
				headings={headings}
				noResults={!loading && sortedContacts.length === 0}
				noResultsMessage="No contacts yet — they'll appear here once a supplier document is parsed.">
				{sortedContacts.map((contact) => {
					const lastUpdated =
						formatTimestamp(contact.updated_at) || formatTimestamp(contact.created_at)
					const typeLabel = labelForType(contact.contact_type)
					const sourceLabel = contact.created_from_document_id
						? "Auto-extracted"
						: "Added manually"

					return (
						<tr key={contact.id}>
							<td>
								{lastUpdated || (
									<span className="supplier-contacts-muted">—</span>
								)}
							</td>
							<td>
								<strong>
									{contact.name || (
										<span className="supplier-contacts-muted">—</span>
									)}
								</strong>
							</td>
							<td>
								{contact.email ? (
									<a href={`mailto:${contact.email}`}>{contact.email}</a>
								) : (
									<span className="supplier-contacts-muted">—</span>
								)}
							</td>
							<td>
								{contact.phone || <span className="supplier-contacts-muted">—</span>}
							</td>
							<td>
								{typeLabel || <span className="supplier-contacts-muted">—</span>}
							</td>
							<td>{sourceLabel}</td>
							<td className="is-button">
								<div className="supplier-contacts-row-actions">
									<Button
										xsSmall={true}
										label="Edit"
										icon={<Pencil size={14} />}
										onClick={() => setEditing(contact)}
									/>
									{user?.is_admin && (
										<Button
											xsSmall={true}
											icon={<Trash2 size={14} />}
											onClick={() => confirmDelete(contact)}
										/>
									)}
								</div>
							</td>
						</tr>
					)
				})}
			</Table>

			{editing && (
				<ContactEditor
					contact={editing}
					contactsPath={contactsPath}
					user={user}
					pushAlert={pushAlert}
					close={() => setEditing(null)}
				/>
			)}

			{adding && (
				<ContactEditor
					contact={null}
					contactsPath={contactsPath}
					supplierKind={supplierKind}
					supplierId={supplierId}
					user={user}
					pushAlert={pushAlert}
					close={() => setAdding(false)}
				/>
			)}
		</div>
	)
}

function ContactEditor({
	contact,
	contactsPath,
	supplierKind,
	supplierId,
	user,
	pushAlert,
	close,
}) {
	const isEdit = !!contact
	const [name, setName] = useState(contact?.name || "")
	const [email, setEmail] = useState(contact?.email || "")
	const [phone, setPhone] = useState(contact?.phone || "")
	const [contactType, setContactType] = useState(contact?.contact_type || "")
	const [saving, setSaving] = useState(false)

	const save = async () => {
		setSaving(true)
		try {
			const trimmedEmail = email.trim().toLowerCase()
			const payload = {
				name: name.trim() || null,
				email: trimmedEmail || null,
				phone: phone.trim() || null,
				contact_type: contactType || null,
				updated_at: firebase.firestore.FieldValue.serverTimestamp(),
				edited_by_user: true,
				edited_by_uid: user?.uid || null,
			}

			if (isEdit) {
				await db.doc(`${contactsPath}/${contact.id}`).update(payload)
			} else {
				// Manual additions get a random doc id so they never collide with
				// the deterministic parser keys (email_/np_ prefixes).
				const docId = `man_${random.generate(12)}`
				await db.doc(`${contactsPath}/${docId}`).set({
					...payload,
					supplier_kind: supplierKind,
					supplier_id: supplierId,
					created_at: firebase.firestore.FieldValue.serverTimestamp(),
					seen_in_document_ids: [],
				})
			}
			close()
		} catch (err) {
			pushAlert({
				type: "ERROR",
				title: "Couldn't save contact",
				body: err?.message || String(err),
			})
		} finally {
			setSaving(false)
		}
	}

	return (
		<Window
			title={isEdit ? "Edit contact" : "Add contact"}
			className="supplier-contacts-window"
			close={close}>
			<div className="supplier-contacts-form">
				<Input
					type="text"
					label="Name"
					placeholder="Name"
					value={name}
					showLabel
					onChange={(value) => setName(value)}
				/>
				<Input
					type="email"
					label="Email"
					placeholder="Email"
					value={email}
					showLabel
					onChange={(value) => setEmail(value)}
				/>
				<Input
					type="text"
					label="Phone"
					placeholder="Phone"
					value={phone}
					showLabel
					onChange={(value) => setPhone(value)}
				/>

				<Select
					label="Type"
					placeholder="Unspecified"
					showLabel
					options={CONTACT_TYPE_OPTIONS}
					value={contactType ? CONTACT_TYPE_OPTIONS[contactType] : ""}
					selected={contactType}
					onSelect={({ option }) => setContactType(option)}
				/>

				<div className="supplier-contacts-form-actions">
					<Button
						label={saving ? "Saving..." : "Save"}
						disabled={saving}
						onClick={save}
					/>
				</div>
			</div>
		</Window>
	)
}
